import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { usePlantingReportResult } from '/@/components/field-list-v2/components/sidebar/hooks';
import { useUnitProvider } from '/@/core/context/unit';
import { useMutateCreateOrUpdateCropMonitoring } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import { useFieldById } from '/@/hooks/fields';
import { plantedAreaForForm } from '/@/hooks/geometry/area';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import NewCropMonitoringFormModal from '../../../../creation-dialog/components/NewCropMonitoringFormModal';
import ExitWithoutSavingModal from '../../../../creation-dialog/ExitWithoutSavingModal';
import type { CropMonitoringFormType } from '../../../../creation-dialog/types';
import { FIELDVIEW_CROP_MAP } from '../../../../creation-dialog/utils';
import type { RegistryAddStep } from './types';

const CreateRegistryPlanting = ({
    open,
    onClose,
    onSuccess,
    setStep,
    fieldId,
    cropMonitoring: cropMonitoringProp,
    initialValues,
}: {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    setStep?: (step: RegistryAddStep) => void;
    fieldId?: number;
    cropMonitoring?: CropMonitoring;
    /** Prefill for the "Registrar lo aplicado" flow — seeds the form from a recommendation and
     *  disables the FieldView planting-report auto-fill. */
    initialValues?: Partial<CropMonitoringFormType>;
}) => {
    const fieldQuery = useFieldById({ id: fieldId ?? 0 }, { enabled: !!fieldId });
    const field = fieldQuery.data;
    const plantingReport = usePlantingReportResult(field);

    let cropMonitoring = cropMonitoringProp ?? field?.activeCropMonitoring;
    if (cropMonitoring?.harvestDate) {
        cropMonitoring = undefined;
    }

    const mappedCrop = (
        plantingReport ? (FIELDVIEW_CROP_MAP[plantingReport.cropName.toLowerCase()] ?? '') : ''
    ) as CropMonitoringFormType['crop'];

    const [showExitWarning, setShowExitWarning] = useState<boolean>(false);
    const units = useUnitProvider();

    const form = useForm<CropMonitoringFormType>({
        defaultValues: {
            plantingDate: cropMonitoring?.plantingDate ?? plantingReport?.plantingDate,
            product:
                cropMonitoring?.cornCropMonitoring?.product ??
                cropMonitoring?.productName ??
                plantingReport?.seedProductNames[0],
            technology: cropMonitoring?.cornCropMonitoring?.technology,
            environment: cropMonitoring?.environment,
            plantedArea: plantedAreaForForm(units, {
                reportHa:
                    cropMonitoring?.plantedArea ?? cropMonitoring?.result?.plantedArea ?? plantingReport?.plantedArea,
                fieldM2: field?.area,
            }),
            density: undefined,
            fieldId: cropMonitoring?.field || field?.id,
            id: cropMonitoring?.id,
            crop: cropMonitoring?.crop || mappedCrop || '',
            previousCrop: cropMonitoring?.previousCrop || '',
            ...initialValues,
        },
    });

    useEffect(() => {
        if (!open) return;
        mpTrackEvent('Add Crop Flow create began', { entry_source: 'field-detail' });
    }, [open]);

    const plantingReportApplied = useRef(false);
    useEffect(() => {
        // When prefilled from a recommendation, keep the seeded values — don't overwrite them
        // with the FieldView planting report.
        if (initialValues) return;
        if (!plantingReport || plantingReportApplied.current) return;
        plantingReportApplied.current = true;
        const crop = (FIELDVIEW_CROP_MAP[plantingReport.cropName.toLowerCase()] ??
            '') as CropMonitoringFormType['crop'];
        form.reset({
            plantingDate: plantingReport.plantingDate,
            product: plantingReport.seedProductNames[0],
            crop,
            previousCrop: '',
            environment: undefined,
            plantedArea: plantingReport.plantedArea,
            density: undefined,
            technology: undefined,
            fieldId: field?.id,
            id: undefined,
        });
    }, [plantingReport, form.reset, field?.id, initialValues]);

    useEffect(() => {
        if (!open) plantingReportApplied.current = false;
    }, [open]);

    const {
        plantingDate,
        environment,
        plantedArea,
        density,
        fieldId: formFieldId,
        technology,
        product,
        crop,
        previousCrop,
    } = form.watch();

    const { trigger: createOrUpdateCropMonitoring, isMutating } = useMutateCreateOrUpdateCropMonitoring(
        {
            plantingDate,
            environment: environment || 0,
            plantedArea,
            density,
            field: field?.id || formFieldId || 0,
            technology,
            product,
            crop: crop || undefined,
            previousCrop: previousCrop === 'none' ? undefined : previousCrop || undefined,
            id: cropMonitoring?.id,
            sourcePlantingReport: plantingReport?.report,
        },
        {
            onSuccess: () => {
                form.reset();
                onSuccess();
            },
        }
    );

    const handleClose = () => {
        if (isMutating) return;
        const hasData =
            crop || plantingDate || environment || plantedArea || density || product || technology || previousCrop;
        if (hasData) {
            setShowExitWarning(true);
        } else {
            onClose();
            form.reset();
        }
    };

    const handleDiscardAndExit = () => {
        setShowExitWarning(false);
        onClose();
        form.reset();
    };

    if (!open) return null;

    return (
        <>
            <NewCropMonitoringFormModal
                open={open && !showExitWarning}
                setClose={handleClose}
                goBack={setStep ? () => setStep('select') : undefined}
                form={form}
                field={field}
                onSubmit={() => createOrUpdateCropMonitoring()}
                submitting={isMutating}
                onlyEdit={false}
                entry_source='field-detail'
                plantingReport={plantingReport ?? undefined}
            />
            <ExitWithoutSavingModal
                open={showExitWarning}
                setClose={() => setShowExitWarning(false)}
                onDiscard={handleDiscardAndExit}
            />
        </>
    );
};

export default CreateRegistryPlanting;
